'use client';

import * as React from 'react';
import { usePathname } from 'next/navigation';
import { useTourStatuses } from '@/api/queries/tours.queries';
import { useWhatsNewBlocksTour } from '@/components/layout/prompt-coordination';
import { pickAutoStartTour } from '@/shared/lib/tours/auto-start';
import { hasSeenTour, startTour, useTourActive } from '@/shared/lib/tours/tour-store';
import { useAuthStore } from '@/shared/stores/auth-store';

// Blocked storage + an in-flight completion POST would otherwise replay the
// tour the moment it ends.
const autoStartedThisSession = new Set<string>();

/**
 * Starts an `autoStart` tour on the first visit to its `path` once the BE lists
 * it as published and not completed on web. Any failure (error, 429, empty
 * list) means no tour. Waits while What's New decides or its popup is open. Renders
 * nothing; sits next to TourLinkWatcher inside the auth-only Suspense block.
 */
export function TourAutoStarter() {
  const pathname = usePathname();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data } = useTourStatuses(isAuthenticated);
  const whatsNewBlocks = useWhatsNewBlocksTour();
  const tourActive = useTourActive();

  React.useEffect(() => {
    if (!data || whatsNewBlocks || tourActive) return;
    const tour = pickAutoStartTour(
      pathname,
      data,
      (t) => autoStartedThisSession.has(t.id) || hasSeenTour(t)
    );
    if (tour && startTour(tour.id, 'auto')) autoStartedThisSession.add(tour.id);
  }, [data, pathname, tourActive, whatsNewBlocks]);

  return null;
}

/** Test-only reset. */
export function resetTourAutoStarterForTests(): void {
  autoStartedThisSession.clear();
}
